import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api/authApi';
import {
  PROMO_ERROR_MESSAGE,
  STEP_TWO_SUBHEADING,
  routeFromSignupCheck,
  parseApiError,
  isExistingAccountError,
} from '../lib/signupFlow';
import { UserPlus, Mail, Loader2, CheckCircle2, Circle, MailCheck, ArrowLeft } from 'lucide-react';
import MarkdownModal from '../components/common/MarkdownModal';
import PasswordInput from '../components/common/PasswordInput';
import AuthLayout from '../components/common/AuthLayout';
import { showSuccessToast, showErrorToast } from '../lib/toast';

const RegisterPage = () => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [accessReason, setAccessReason] = useState('whitelisted');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState('');
  const [promoError, setPromoError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const { register, resendConfirmation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      setPromoCode(code);
      setShowPromoCode(true);
    }
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [searchParams, location.state?.email]);

  const passwordChecks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };

  const isPasswordValid =
    passwordChecks.minLength && passwordChecks.hasUppercase && passwordChecks.hasNumber;

  const runSignupCheck = async (promo = promoCode.trim() || null) => {
    setError('');
    setPromoError('');
    setIsLoading(true);

    try {
      const result = await authApi.checkSignup(email, promo);
      const outcome = routeFromSignupCheck(result, {
        email,
        promoCode: promo || '',
        navigate,
        setAccessReason,
        setStep,
      });
      if (outcome === 'promo_error') {
        setPromoError(PROMO_ERROR_MESSAGE);
      }
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailContinue = (e) => {
    e.preventDefault();
    void runSignupCheck();
  };

  const clearPromo = () => {
    setPromoCode('');
    setShowPromoCode(false);
    setPromoError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreeToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy to register.');
      return;
    }

    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements.');
      return;
    }

    setIsLoading(true);

    try {
      const data = await register(email, password, null, promoCode.trim() || null);
      if (data?.session) {
        showSuccessToast('Account created.');
        navigate('/');
      } else {
        setConfirmationSent(true);
      }
    } catch (err) {
      const errorMessage = parseApiError(err, 'Registration failed. Please try again.');
      if (isExistingAccountError(errorMessage)) {
        navigate('/login', { state: { email, existingAccount: true } });
        return;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await resendConfirmation(email);
      showSuccessToast('Confirmation email sent.');
    } catch (err) {
      showErrorToast(err?.message || 'Could not resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const footer = (
    <p className="text-center text-[13px] text-gray-400 dark:text-gray-500">
      <button
        onClick={() => setTermsModalOpen(true)}
        className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
      >
        Terms
      </button>
      {' · '}
      <button
        onClick={() => setPrivacyModalOpen(true)}
        className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
      >
        Privacy
      </button>
    </p>
  );

  const subheading = confirmationSent
    ? 'You can log in once your email is confirmed.'
    : step === 'email'
      ? 'Enter your email to get started. If you have a promo code, you can add it here.'
      : STEP_TWO_SUBHEADING[accessReason];

  return (
    <AuthLayout footer={footer}>
      <div className="space-y-2">
        <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">
          {confirmationSent ? 'Check your inbox' : 'Create your account'}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">{subheading}</p>
      </div>

      {confirmationSent ? (
        <div className="mt-8 space-y-6">
          <div className="flex flex-col items-center gap-3 p-6 bg-emerald-50/80 dark:bg-emerald-950/40 backdrop-blur-sm border border-emerald-200 dark:border-emerald-900/50 rounded-2xl text-center">
            <MailCheck size={36} className="text-emerald-600 dark:text-emerald-400" />
            <p className="text-sm text-gray-700 dark:text-gray-300">
              We sent a confirmation link to{' '}
              <span className="font-semibold text-gray-900 dark:text-white">{email}</span>.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Didn&apos;t get it? Check your spam folder or resend below.
            </p>
          </div>

          <button
            type="button"
            onClick={handleResend}
            disabled={resendLoading}
            className="w-full flex justify-center items-center gap-2 py-3 px-6 text-sm font-bold rounded-xl border-2 border-emerald-500 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resendLoading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                <span>Sending...</span>
              </>
            ) : (
              <span>Resend confirmation email</span>
            )}
          </button>

          <div className="text-center pt-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Already confirmed?{' '}
              <Link
                to="/login"
                className="font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors underline-offset-4 hover:underline"
              >
                Log in
              </Link>
            </p>
          </div>
        </div>
      ) : step === 'email' ? (
        <form className="mt-8 space-y-6" onSubmit={handleEmailContinue}>
          {error && (
            <div className="bg-red-50/80 dark:bg-red-950/40 backdrop-blur-sm border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 text-sm font-semibold p-4 rounded-xl">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <Mail size={18} />
              </div>
              <input
                type="email"
                required
                className="block w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 transition-colors"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {showPromoCode ? (
              <div className="space-y-2">
                <input
                  type="text"
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 transition-colors"
                  placeholder="Promo code (optional)"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setPromoError('');
                  }}
                  autoComplete="off"
                />
                {promoError && (
                  <>
                    <p className="text-sm font-semibold text-red-600 dark:text-red-400">{promoError}</p>
                    <button
                      type="button"
                      onClick={clearPromo}
                      className="text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                      Continue without code
                    </button>
                  </>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowPromoCode(true)}
                className="mx-auto block text-sm font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
              >
                Have a promo code?
              </button>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center items-center gap-2 py-4 px-6 text-sm font-bold rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/30 hover:scale-[1.02] active:scale-[0.98]"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                <span>Checking...</span>
              </>
            ) : (
              <span>Continue</span>
            )}
          </button>

          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors underline-offset-4 hover:underline"
            >
              Log in
            </Link>
          </p>
        </form>
      ) : (
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50/80 dark:bg-red-950/40 backdrop-blur-sm border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 text-sm font-semibold p-4 rounded-xl">
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
            <Mail size={16} className="shrink-0 text-gray-400" />
            <span className="font-medium">{email}</span>
          </div>

          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          {password && (
            <div className="p-4 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border-2 border-gray-200 dark:border-gray-700">
              <p className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                Password requirements
              </p>
              <ul className="space-y-2.5">
                {[
                  [passwordChecks.minLength, 'At least 8 characters'],
                  [passwordChecks.hasNumber, 'At least one number'],
                  [passwordChecks.hasUppercase, 'At least one uppercase letter'],
                ].map(([ok, label]) => (
                  <li
                    key={label}
                    className={`flex items-center gap-3 text-sm ${ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`}
                  >
                    {ok ? (
                      <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
                    ) : (
                      <Circle size={18} className="text-gray-400 shrink-0" />
                    )}
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-start gap-3 p-4 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border-2 border-gray-200 dark:border-gray-700">
            <div className="flex items-center h-5 pt-0.5">
              <input
                id="agree-to-terms"
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="w-5 h-5 text-emerald-600 bg-white border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500/30 focus:ring-offset-0 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
              />
            </div>
            <label htmlFor="agree-to-terms" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer leading-relaxed">
              I agree to the{' '}
              <button type="button" onClick={() => setTermsModalOpen(true)} className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 font-semibold hover:underline">
                Terms of Service
              </button>
              {' '}and{' '}
              <button type="button" onClick={() => setPrivacyModalOpen(true)} className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 font-semibold hover:underline">
                Privacy Policy
              </button>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading || !isPasswordValid || !agreeToTerms}
            className="group relative w-full flex justify-center items-center gap-2 py-4 px-6 text-sm font-bold rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/30 hover:scale-[1.02] active:scale-[0.98]"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                <span>Creating account...</span>
              </>
            ) : (
              <>
                <UserPlus size={20} />
                <span>Create account</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setStep('email')}
            className="mx-auto flex w-fit items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </form>
      )}

      <MarkdownModal isOpen={termsModalOpen} onClose={() => setTermsModalOpen(false)} markdownUrl="/terms-of-service.md" />
      <MarkdownModal isOpen={privacyModalOpen} onClose={() => setPrivacyModalOpen(false)} markdownUrl="/privacy-policy.md" />
    </AuthLayout>
  );
};

export default RegisterPage;
